/**
 * Anomaly hotspots for M2's mining (M3 plan §7 item 6, slice R22i). The Reach streams one M1 cluster around every
 * anomaly (`hotspotCluster`); in a tetherstone-vein system, a fracture rock inside a hotspot's bounds carries
 * tetherstone with `bridge-mining.json hotspotTetherstoneChance` (M2's roll, `rockComposition`), everywhere else M2's
 * own `mining.json tetherstone.chance` holds. The bridge hands the rule to `RocksRuntime.setTetherstoneChance` on every
 * system entry, before the entered system streams.
 *
 * M8 MC-a9: the signature hotspot mix lives in `fieldMix.ts` (its multiplier and cap are
 * `galaxy.json minerals.hotspot.mult` and `stackCap`), and `gradeDangerBlend` here sets how much of the richest field's
 * `minerals.dangerByGrade` reaches the interdiction rate (`encounters.ts`).
 */
import { z } from 'zod'
import rawConfig from '../../data/bridge-mining.json'
import type { FieldConfig } from '../../data/schemas'
import type { SystemLayout } from '../../galaxy/layout/types'
import { clusterBounds, hotspotCluster } from '../../streaming'

export const bridgeMiningSchema = z
  .object({
    comment: z.string().optional(),
    hotspotTetherstoneChance: z.number().min(0).max(1),
    /** How much of `galaxy.json minerals.dangerByGrade` reaches the interdiction rate (MC-a9): 0 neutral, 1 full. */
    gradeDangerBlend: z.number().min(0).max(1),
  })
  .strict()
export type BridgeMiningConfig = z.infer<typeof bridgeMiningSchema>

export function loadBridgeMiningConfig(raw: unknown = rawConfig): BridgeMiningConfig {
  return bridgeMiningSchema.parse(raw)
}

/** The archetype whose anomalies carry fracture-only tetherstone (D-086: seed 1's vein is `sys-33`). */
const VEIN = 'tetherstone-vein'

/** A rock position → its fracture tetherstone chance, or a negative value for M2's default. */
export type TetherChanceAt = (x: number, y: number, z: number) => number

/**
 * The hotspot rule of a system: `chance` inside any anomaly hotspot's bounds, else −1 (the default). Null outside
 * tetherstone-vein systems or with no anomaly. The returned function is allocation-free.
 */
export function hotspotTetherChance(
  layout: Pick<SystemLayout, 'archetype' | 'pois'>,
  fieldCfg: FieldConfig,
  chance: number,
): TetherChanceAt | null {
  if (layout.archetype !== VEIN) return null
  const anomalies = layout.pois.filter((p) => p.kind === 'anomaly')
  if (anomalies.length === 0) return null
  const n = anomalies.length
  const centres = new Float64Array(n * 3)
  const radii2 = new Float64Array(n)
  for (let i = 0; i < n; i++) {
    const b = clusterBounds(hotspotCluster(anomalies[i], fieldCfg).site, fieldCfg)
    centres[i * 3] = b.center[0]
    centres[i * 3 + 1] = b.center[1]
    centres[i * 3 + 2] = b.center[2]
    radii2[i] = b.radiusM * b.radiusM
  }
  return (x, y, z) => {
    for (let i = 0; i < n; i++) {
      const dx = x - centres[i * 3]
      const dy = y - centres[i * 3 + 1]
      const dz = z - centres[i * 3 + 2]
      if (dx * dx + dy * dy + dz * dz <= radii2[i]) return chance
    }
    return -1
  }
}
